import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { resetAppClock, synchronizeAppClock } from '@/lib/date-utils';

interface LocalUser {
  id: string;
  email: string;
  nome?: string;
  perfil?: string;
  role?: string;
  cliente_id?: string;
  clinica_id?: string;
  clinicas_ids?: string[];
  dentistas_por_clinica?: Record<string, string>;
  permissoes_extra?: string[];
  permissoes_por_clinica?: Record<string, string[]>;
  exigir_troca_senha?: boolean;
  mfa_obrigatorio?: boolean;
  mfa_validado?: boolean;
  sessao_id?: string;
}

interface AuthContextType {
  user: LocalUser | null;
  session: { user: LocalUser } | null;
  loading: boolean;
  signIn: (email: string, password: string, mfaCode?: string) => Promise<{ error: any; mfaRequired?: boolean }>;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  switchClinic: (clinicaId: string) => Promise<{ error: any; clinica?: { id: string; nome: string; cliente_id: string } }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const isSuperAdminUser = (user: LocalUser) =>
  user.role === "superadmin" || user.email === "suporte@cloudservidores.com";

const syncDefaultClinic = (user: LocalUser) => {
  if (typeof window === "undefined" || isSuperAdminUser(user)) return;

  const current = localStorage.getItem("odonto.activeClinicaId") || "";
  const allowed = Array.from(new Set([...(user.clinicas_ids || []), user.clinica_id].filter(Boolean))) as string[];
  const next = allowed.includes(current)
    ? current
    : user.clinica_id && allowed.includes(user.clinica_id)
      ? user.clinica_id
      : allowed[0] || "";

  if (next) localStorage.setItem("odonto.activeClinicaId", next);
  else localStorage.removeItem("odonto.activeClinicaId");
  if (user.cliente_id) localStorage.setItem("odonto.activeClienteId", user.cliente_id);

  if (next !== current) {
    window.dispatchEvent(new Event("odonto-clinic-change"));
  }
};

const clearActiveClinic = () => {
  if (typeof window === "undefined") return;
  localStorage.removeItem("odonto.activeClinicaId");
  localStorage.removeItem("odonto.activeClinicaName");
  localStorage.removeItem("odonto.activeClienteId");
  window.dispatchEvent(new Event("odonto-clinic-change"));
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<LocalUser | null>(null);
  const [session, setSession] = useState<{ user: LocalUser } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/auth.php?action=session', { credentials: 'include', cache: 'no-store' })
      .then((response) => {
        synchronizeAppClock(response.headers.get('Date'));
        return response.ok ? response.json() : Promise.reject();
      })
      .then((data) => {
        if (data?.user) {
          syncDefaultClinic(data.user);
          setUser(data.user);
          setSession({ user: data.user });
        } else {
          setUser(null);
          setSession(null);
        }
      })
      .catch(() => {
        clearActiveClinic();
        setUser(null);
        setSession(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const signIn = async (email: string, password: string, mfaCode?: string) => {
    try {
      const response = await fetch('/api/auth.php?action=login', {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password, mfa_code: mfaCode || "" }),
      });
      synchronizeAppClock(response.headers.get('Date'));
      const data = await response.json().catch(() => ({}));
      if (response.status === 202 || data?.mfa_required) {
        return { error: { message: data?.error || 'Codigo MFA obrigatorio' }, mfaRequired: true };
      }
      if (!response.ok || !data?.user) {
        return { error: { message: data?.error || 'Invalid login credentials' } };
      }
      clearActiveClinic();
      syncDefaultClinic(data.user);
      setUser(data.user);
      setSession({ user: data.user });
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const signUp = async (email: string, password: string) => {
    return { error: { message: 'Cadastro publico desativado' } };
  };

  const switchClinic = async (clinicaId: string) => {
    try {
      const response = await fetch('/api/auth.php?action=switch_clinic', {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clinica_id: clinicaId }),
      });
      synchronizeAppClock(response.headers.get('Date'));
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.user || !data?.clinica) {
        return { error: { message: data?.error || 'Nao foi possivel trocar de unidade' } };
      }

      localStorage.setItem('odonto.activeClinicaId', data.clinica.id);
      localStorage.setItem('odonto.activeClinicaName', data.clinica.nome || '');
      localStorage.setItem('odonto.activeClienteId', data.clinica.cliente_id || data.user.cliente_id || '');
      setUser(data.user);
      setSession({ user: data.user });
      window.dispatchEvent(new Event('odonto-clinic-change'));
      return { error: null, clinica: data.clinica };
    } catch (error) {
      return { error };
    }
  };

  const signOut = async () => {
    // A saída visual é imediata e não depende da disponibilidade momentânea da API.
    clearActiveClinic();
    resetAppClock();
    setSession(null);
    setUser(null);

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 5000);
    try {
      const response = await fetch('/api/auth.php?action=logout', {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
        keepalive: true,
        headers: { 'Accept': 'application/json' },
        signal: controller.signal,
      });
      if (!response.ok) console.warn('Servidor não confirmou o encerramento da sessão');
    } catch (error) {
      console.warn('Sessão local encerrada; confirmação remota indisponível', error);
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    switchClinic,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
